import { ComponentProps, useMemo } from 'react';

import { ModelCreateModal, ModelFieldGroup } from '@rhino-project/ui-heroui';
import { useModelIndex, useModelShow } from '@rhino-project/core/hooks';
import { useWatch } from 'react-hook-form';

const RosterStatusField = (props: ComponentProps<typeof ModelFieldGroup>) => {
  // The team_season reference field stores only the identifier, so resolve the
  // record to read its status and offer just the roster statuses that match:
  // planning seasons use planning statuses, active seasons use active ones.
  const teamSeasonId = useWatch({ name: 'team_season' });
  const { resource: teamSeason } = useModelShow('team_season', teamSeasonId, {
    queryOptions: { enabled: !!teamSeasonId }
  });
  const status = (teamSeason as { status?: string } | null)?.status;

  const filter = useMemo(() => {
    return { status_type: status };
  }, [status]);

  // Roster statuses are meaningful in their `order` column, not insertion order.
  return (
    <ModelFieldGroup
      disabled={!status}
      filter={filter}
      order={['order']}
      {...props}
    />
  );
};

interface TeamSeasonFieldProps extends ComponentProps<typeof ModelFieldGroup> {
  playerId?: string | number;
}

const TeamSeasonField = ({ playerId, ...props }: TeamSeasonFieldProps) => {
  // This form adds the player (parentId) to a team_season. Exclude the
  // team_seasons the player is already rostered on, and list the rest
  // most-recent-season first.
  const { results: rosters } = useModelIndex('roster', {
    filter: { player_id: playerId },
    queryOptions: { enabled: !!playerId }
  });

  const filter = useMemo(() => {
    const onIds = (
      (rosters as { team_season: { id: number } }[] | undefined) ?? []
    ).map((r) => r.team_season.id);

    return {
      status: ['active', 'planning'],
      ...(onIds.length > 0 && { id: { diff: onIds } })
    };
  }, [rosters]);

  return (
    <ModelFieldGroup filter={filter} order={['-season.starts_on']} {...props} />
  );
};

const RosterSidebarForm = (props: ComponentProps<typeof ModelCreateModal>) => {
  const parentId = props.parentId as string | number | undefined;

  const paths = useMemo(() => {
    return [
      // eslint-disable-next-line @eslint-react/no-missing-key
      <TeamSeasonField path="team_season" playerId={parentId} />,
      // eslint-disable-next-line @eslint-react/no-missing-key
      <RosterStatusField path="roster_status" />
    ];
  }, [parentId]);

  return <ModelCreateModal {...props} model="roster" paths={paths} />;
};

export default RosterSidebarForm;
